
import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthProvider';
import ChatBox from '@/components/chat/ChatBox';
import { useAutoClaimTransfers } from '@/hooks/useAutoClaimTransfers';
import { useAuthSubmit } from '@/hooks/auth/useAuthSubmit';
import { supabase } from '@/integrations/supabase/client';
import SignInForm, { SignInFormValues } from '@/components/auth/SignInForm';
import SignInHeader from '@/components/auth/SignInHeader';
import PolicyLinks from '@/components/auth/PolicyLinks';

const SignIn = () => {
  const { loading, user } = useAuth();
  const { handleSignIn } = useAuthSubmit();
  const { checkPendingTransfers } = useAutoClaimTransfers();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get redirectTo from query params
  const searchParams = new URLSearchParams(location.search);
  const redirectTo = searchParams.get('redirectTo') || '/home';
  
  // Check for any existing session
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        
        if (data.session) {
          console.log("Found existing session, redirecting to:", redirectTo);
          navigate(redirectTo);
        }
      } catch (error) {
        console.error("Error checking session:", error);
      }
    };
    
    checkExistingSession();
  }, [navigate, redirectTo]);
  
  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      console.log("User is logged in, redirecting to:", redirectTo);
      navigate(redirectTo);
    }
  }, [user, navigate, redirectTo]);

  const onSubmit = async (data: SignInFormValues) => {
    try {
      console.log("Attempting to sign in with:", data.email);
      // Ensure required fields are present
      if (!data.email || !data.password) {
        console.error('Email and password are required');
        return;
      }
      
      await handleSignIn({
        email: data.email,
        password: data.password
      });
      
      // Check for pending transfers that match this email
      await checkPendingTransfers();
      
      navigate(redirectTo);
    } catch (error) {
      console.error('Login error:', error);
      // Error handling is already done in the signIn function
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-500 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <SignInHeader />
      <div className="mt-4">
        <PolicyLinks />
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <SignInForm onSubmit={onSubmit} loading={loading} />
        </div>
      </div>

      <ChatBox />
    </div>
  );
};

export default SignIn;
